// PRD 06 §3.2 — group module items by `area` for the per-module table.
// Ordering rules mirror the prototype `buildAreaTableHtml` (module-config.html
// L641–L725):
//   1. Areas declared in `mod.areaOrder` (user-customized) come first, in that
//      order, but only when present in the current item set.
//   2. Then areas from the global DEFAULT_AREA_ORDER that are present and not
//      yet listed.
//   3. Then any remaining present areas in first-seen order.
// Within each area, items keep their order from `mod.itemIds`.

import type { Module, ProItemRef } from '../types/pro-deployment.types';

// PRD-defined canonical area names. Matches prototype L639.
export const DEFAULT_AREA_ORDER: readonly string[] = [
  '정서·인지 증상 관리',
  '신체·생활습관 요인 관리',
  '사회·환경적 지원',
  '자기관리 및 가족·자원 연계',
];

const FALLBACK_AREA = '미분류';

export interface AreaGroup {
  name: string;
  items: ProItemRef[];
  totalQuestions: number;
}

export interface ModuleAreaBreakdown {
  groups: AreaGroup[];
  totalQuestions: number;
}

// Returns the items for a module in the order stored on `mod.itemIds`.
// Items not present in the dictionary are silently dropped (defensive).
export function resolveModuleItems(
  module: Module,
  itemDict: ReadonlyMap<string, ProItemRef>,
): ProItemRef[] {
  const result: ProItemRef[] = [];
  for (const pid of module.itemIds) {
    const item = itemDict.get(pid);
    if (item) result.push(item);
  }
  return result;
}

// PRD 06 §6 — area drag-and-drop within a module. Reorders `sortedAreas`
// (the resolved area list from `buildModuleAreaBreakdown`) so that
// `sourceArea` is placed immediately before/after `targetArea`. Returns the
// new full ordering — caller PATCHes this as the module's `areaOrder`.
export function moveAreaTo(
  sortedAreas: readonly string[],
  sourceArea: string,
  targetArea: string,
  placeAfter: boolean,
): string[] {
  if (sourceArea === targetArea) return [...sortedAreas];
  const order = [...sortedAreas];
  const sIdx = order.indexOf(sourceArea);
  if (sIdx === -1) return [...sortedAreas];
  order.splice(sIdx, 1);
  let tIdx = order.indexOf(targetArea);
  if (tIdx === -1) return [...sortedAreas];
  if (placeAfter) tIdx += 1;
  order.splice(tIdx, 0, sourceArea);
  return order;
}

// PRD 06 §6 — item drag-and-drop within the same area. Reorders
// `currentItemIds` (the module's full ordered PID list) by removing
// `sourceItemId` and re-inserting it adjacent to `targetItemId`.
// Caller MUST validate same-area before invoking.
export function moveItemTo(
  currentItemIds: readonly string[],
  sourceItemId: string,
  targetItemId: string,
  placeAfter: boolean,
): string[] {
  if (sourceItemId === targetItemId) return [...currentItemIds];
  const ids = [...currentItemIds];
  const sIdx = ids.indexOf(sourceItemId);
  if (sIdx === -1) return [...currentItemIds];
  ids.splice(sIdx, 1);
  let tIdx = ids.indexOf(targetItemId);
  if (tIdx === -1) return [...currentItemIds]; // target vanished — abort.
  if (placeAfter) tIdx += 1;
  ids.splice(tIdx, 0, sourceItemId);
  return ids;
}

// Drops areas from `areaOrder` that no longer have any items in `nextItems`.
// The server (mock + future BE) rejects PATCH /modules/{mid} when areaOrder
// references an absent area, so the caller must prune before sending.
export function pruneAreaOrder(
  areaOrder: readonly string[],
  nextItems: readonly ProItemRef[],
): string[] {
  const present = new Set(nextItems.map((i) => i.area));
  return areaOrder.filter((a) => present.has(a));
}

export function buildModuleAreaBreakdown(
  module: Module,
  itemDict: ReadonlyMap<string, ProItemRef>,
): ModuleAreaBreakdown {
  const items = resolveModuleItems(module, itemDict);
  // First pass: insertion-ordered grouping by area.
  const byArea = new Map<string, ProItemRef[]>();
  const insertionOrder: string[] = [];
  for (const item of items) {
    const area = item.area || FALLBACK_AREA;
    const bucket = byArea.get(area);
    if (bucket) {
      bucket.push(item);
    } else {
      byArea.set(area, [item]);
      insertionOrder.push(area);
    }
  }

  // Second pass: sort area names according to (user override → default → rest).
  const presentAreas = new Set(insertionOrder);
  const userOrder = module.areaOrder.filter((a) => presentAreas.has(a));
  const sorted: string[] = [];
  const seen = new Set<string>();
  for (const a of userOrder) {
    if (!seen.has(a)) {
      sorted.push(a);
      seen.add(a);
    }
  }
  for (const a of DEFAULT_AREA_ORDER) {
    if (presentAreas.has(a) && !seen.has(a)) {
      sorted.push(a);
      seen.add(a);
    }
  }
  for (const a of insertionOrder) {
    if (!seen.has(a)) {
      sorted.push(a);
      seen.add(a);
    }
  }

  const groups: AreaGroup[] = sorted.map((name) => {
    const areaItems = byArea.get(name) ?? [];
    const totalQuestions = areaItems.reduce((acc, it) => acc + it.questionCount, 0);
    return { name, items: areaItems, totalQuestions };
  });
  const totalQuestions = groups.reduce((acc, g) => acc + g.totalQuestions, 0);

  return { groups, totalQuestions };
}
